import axios from "axios";
import { useEffect, useState } from "react";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";

export type FranchiseLogoBrand = { name: string; logoUrl: string | null; website: string | null };
export type FranchiseLogoMap = Record<string, FranchiseLogoBrand>;

// One request per session, shared by every table: the map is a few hundred entries at most, and
// fetching it once beats populating the franchise on every establishment of every list endpoint.
let pending: Promise<FranchiseLogoMap> | null = null;
let loaded: FranchiseLogoMap | null = null;

function loadFranchiseLogos(): Promise<FranchiseLogoMap> {
    if (!pending) {
        pending = axios
            .get<FranchiseLogoMap>(`${API_URL}/franchises/logos`, {
                headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
            })
            .then((res) => (loaded = res.data))
            // A failed load just means no back-office logos this time; let a later mount retry.
            .catch(() => { pending = null; return {}; });
    }
    return pending;
}

/** franchiseId → the brand's name, back-office logo and website. Empty until loaded; rows re-render once it arrives. */
export function useFranchiseLogoMap(): FranchiseLogoMap {
    const [map, setMap] = useState<FranchiseLogoMap>(loaded ?? {});
    useEffect(() => {
        if (loaded) return;
        let alive = true;
        loadFranchiseLogos().then((m) => { if (alive) setMap(m); });
        return () => { alive = false; };
    }, []);
    return map;
}

/** An establishment's `franchise` comes as a bare id or populated `{ _id, name }` depending on the endpoint. */
export function franchiseIdOf(franchise: unknown): string | null {
    if (!franchise) return null;
    if (typeof franchise === "string") return franchise;
    const id = (franchise as { _id?: unknown })._id;
    return id ? String(id) : null;
}
